import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/api/auth/login', credentials);
    if (data?.mfaRequired) {
      return { mfaRequired: true, mfaToken: data.mfaToken };
    }
    if (data?.mfaEnrollRequired) {
      return { mfaEnrollRequired: true, enrollPortal: data.enrollPortal };
    }
    localStorage.setItem('cloudToken', data.token);
    localStorage.setItem('cloudUser', JSON.stringify(data.user));
    return data;
  } catch (err) {
    const resData = err.response?.data;
    if (resData?.upgradeRequired) {
      return rejectWithValue({ message: resData.error, upgradeRequired: true, currentPlan: resData.currentPlan });
    }
    return rejectWithValue({ message: resData?.error || 'Login failed' });
  }
});

export const getMe = createAsyncThunk('auth/getMe', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/api/auth/me');
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Failed to fetch user');
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  try { await api.post('/api/auth/logout'); } catch (_) { /* ignore */ }
  localStorage.removeItem('cloudToken');
  localStorage.removeItem('cloudUser');
});

const savedUser = JSON.parse(localStorage.getItem('cloudUser') || 'null');
const savedToken = localStorage.getItem('cloudToken');

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: savedUser,
    token: savedToken,
    isAuthenticated: !!savedToken,
    loading: false,
    error: null,
    mfaRequired: false,
    mfaEnrollRequired: false,
    mfaToken: null,
    enrollPortal: null
  },
  reducers: {
    clearError: (state) => { state.error = null; },
    clearMfa: (state) => {
      state.mfaRequired = false;
      state.mfaEnrollRequired = false;
      state.mfaToken = null;
      state.enrollPortal = null;
    },
    mfaVerified: (state, action) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.mfaRequired = false;
      state.mfaEnrollRequired = false;
      state.mfaToken = null;
      state.enrollPortal = null;
      localStorage.setItem('cloudToken', action.payload.token);
      localStorage.setItem('cloudUser', JSON.stringify(action.payload.user));
    },
    // Consume an SSO token handed over from portal.sentrivox.com.
    // The user object is populated separately via getMe().
    ssoConsumeToken: (state, action) => {
      const token = action.payload;
      if (!token) return;
      state.token = token;
      state.isAuthenticated = true;
      state.error = null;
      try { localStorage.setItem('cloudToken', token); } catch (_) { /* ignore */ }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.mfaRequired) {
          state.mfaRequired = true;
          state.mfaToken = action.payload.mfaToken;
        } else if (action.payload?.mfaEnrollRequired) {
          state.mfaEnrollRequired = true;
          state.enrollPortal = action.payload.enrollPortal;
        } else {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.token = action.payload.token;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(getMe.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });
  }
});

export const { clearError, clearMfa, mfaVerified, ssoConsumeToken } = authSlice.actions;
export default authSlice.reducer;
